// app/screens/business/finance/BusinessStripePayment.js (Stripe form; gated off on iOS + Android, web/dev only)
import React, { useState, useEffect } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, Alert, ActivityIndicator,
  ScrollView, SafeAreaView, Dimensions,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

// Stripe imports — will work when @stripe/stripe-react-native is installed
let CardField = null, useConfirmPayment = null, useStripe = null;
try {
  const stripe = require('@stripe/stripe-react-native');
  CardField         = stripe.CardField;
  useConfirmPayment = stripe.useConfirmPayment;
  useStripe         = stripe.useStripe;
} catch {
  console.warn('[Stripe] @stripe/stripe-react-native not installed — using mock mode');
}

const { width } = Dimensions.get('window');
const API_BASE  = 'https://api.jobeng.ai/api';
const API_KEY   = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// Shared neutrals + status colors. Brand accents (primary/p2/gold/purple),
// background, header label, and VAT behaviour differ per variant so business
// and professional checkout look and read distinctly — same payment engine.
const SHARED = {
  success: '#10B981',
  warning: '#F59E0B',
  danger:  '#EF4444',
  card:    '#FFFFFF',
  border:  '#E2E8F0',
  text:    '#1E293B',
  sub:     '#64748B',
  light:   '#94A3B8',
};

const THEMES = {
  // Business: navy + gold, VAT invoice line (B2B).
  business: {
    ...SHARED,
    label:   'Business Payment',
    primary: '#0e4e96',
    p2:      '#1e7be2',
    gold:    '#fdb913',
    purple:  '#7C3AED',
    bg:      '#F0F4FA',
    vat:     true,
  },
  // Professional: teal, consumer-style checkout (price shown inclusive, no VAT line).
  professional: {
    ...SHARED,
    label:   'Checkout',
    primary: '#0D9488',
    p2:      '#14B8A6',
    gold:    '#F59E0B',
    purple:  '#6366F1',
    bg:      '#F0FAF8',
    vat:     false,
  },
};

const getHeaders = async () => {
  const tok = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token') || '';
  return { 'X-API-Key': API_KEY, 'Authorization': `Bearer ${tok}`, 'Content-Type': 'application/json' };
};

const fmtCurrency = (pence, cur = 'GBP') => `£${(pence / 100).toFixed(2)}`;

// Mock card for when Stripe SDK not installed
const MockCardField = ({ onCardChange, C, styles }) => (
  <View style={styles.mockCard}>
    <MaterialCommunityIcons name="credit-card-outline" size={20} color={C.sub}/>
    <Text style={styles.mockCardText}>4242 4242 4242 4242  12/26  123</Text>
    <Text style={styles.mockCardNote}>(Test card — Stripe SDK not installed)</Text>
  </View>
);

export default function BusinessStripePayment({ scenario, variant = 'business', onPaymentSuccess, onPaymentFailure, onCancel, onFreeTrialStart }) {
  if (Platform.OS === 'ios' || Platform.OS === 'android') return null;
  const C = THEMES[variant] || THEMES.business;
  const styles = makeStyles(C);
  const stripeAvailable = !!CardField;

  // Only call Stripe hooks if SDK is available
  const stripeHooks = stripeAvailable ? useConfirmPayment() : [null, false];
  const [confirmPaymentFn, stripeLoading] = Array.isArray(stripeHooks) ? stripeHooks : [null, false];

  const [cardDetails,   setCardDetails]   = useState(null);
  const [clientSecret,  setClientSecret]  = useState(null);
  const [processing,    setProcessing]    = useState(false);
  const [initError,     setInitError]     = useState(null);
  const [trialEligible, setTrialEligible] = useState(false);
  const [showTrial,     setShowTrial]     = useState(false);
  const [step,          setStep]          = useState('card'); // card | processing | success | failed

  if (!scenario) {
    return (
      <SafeAreaView style={styles.errorContainer}>
        <MaterialCommunityIcons name="alert-circle" size={48} color={C.danger}/>
        <Text style={styles.errorText}>No payment scenario provided</Text>
      </SafeAreaView>
    );
  }

  useEffect(() => {
    checkTrialEligibility();
    initPaymentIntent();
  }, []);

  const checkTrialEligibility = async () => {
    try {
      const userId  = await AsyncStorage.getItem('user_id') || await AsyncStorage.getItem('userId') || '';
      const cid     = scenario.companyId || scenario.metadata?.company_id || '';
      const trialKey= `free_trial_used_${userId}_${cid}`;
      const used    = await AsyncStorage.getItem(trialKey);
      const eligible = !used && scenario.type === 'subscription' && parseFloat(scenario.amountInPence||0) > 0;
      setTrialEligible(eligible);
      if (eligible) setShowTrial(true);
    } catch {}
  };

  const initPaymentIntent = async () => {
    if (scenario.amountInPence === 0) return; // free / trial
    setProcessing(true);
    try {
      const h   = await getHeaders();
      const res = await fetch(`${API_BASE}/stripe/payment-intent`, {
        method: 'POST', headers: h,
        body: JSON.stringify({
          amount:      scenario.amountInPence,
          currency:    scenario.currency || 'gbp',
          description: scenario.name,
          company_id:  scenario.companyId || scenario.metadata?.company_id,
          metadata:    scenario.metadata || {},
        }),
      });
      const data = await res.json();
      if (data.clientSecret) {
        setClientSecret(data.clientSecret);
      } else {
        // Backend not configured — use test mode
        setClientSecret('pi_test_mock_secret_for_development');
      }
    } catch {
      // No payment backend yet — allow test flow
      setClientSecret('pi_test_mock_secret_for_development');
    } finally {
      setProcessing(false);
    }
  };

  const handleStartFreeTrial = async () => {
    setProcessing(true);
    try {
      const userId  = await AsyncStorage.getItem('user_id') || 'user_default';
      const cid     = scenario.companyId || scenario.metadata?.company_id || 'company_default';
      const trialKey= `free_trial_used_${userId}_${cid}`;
      const endDate = new Date(Date.now() + 14*24*60*60*1000);

      const trialData = {
        userId, companyId: cid, planId: scenario.id, planName: scenario.name,
        startDate: new Date().toISOString(), endDate: endDate.toISOString(),
        status: 'trial', originalAmountPence: scenario.amountInPence,
      };

      await AsyncStorage.setItem(trialKey, JSON.stringify(trialData));
      await AsyncStorage.setItem('business_subscription', JSON.stringify({
        plan_id:      scenario.id,
        plan_name:    scenario.name,
        plan_type:    scenario.planType || 'single_user',
        price:        0,
        status:       'trial',
        end_date:     endDate.toISOString().split('T')[0],
        start_date:   new Date().toISOString().split('T')[0],
        trial:        true,
      }));

      setShowTrial(false);
      setStep('success');
      onFreeTrialStart?.(trialData);

      Alert.alert(
        '🎉 14-Day Free Trial Started!',
        `Full access to ${scenario.name} until ${endDate.toLocaleDateString('en-GB')}.\n\nWe'll remind you 7 days before your trial ends.`,
        [{ text: 'Get Started', onPress: () => onPaymentSuccess?.({ trial: true, scenario }) }]
      );
    } catch (e) {
      Alert.alert('Error', e.message);
    } finally {
      setProcessing(false);
    }
  };

  const handlePay = async () => {
    if (!cardDetails?.complete && stripeAvailable) {
      Alert.alert('Card Details', 'Please complete your card details.');
      return;
    }
    setProcessing(true);
    setStep('processing');
    try {
      let paymentResult;

      if (stripeAvailable && confirmPaymentFn && clientSecret && clientSecret !== 'pi_test_mock_secret_for_development') {
        // Real Stripe payment
        const { paymentIntent, error } = await confirmPaymentFn(clientSecret, {
          paymentMethodType: 'Card',
          paymentMethodData: { billingDetails: {} },
        });
        if (error) throw new Error(error.message);
        paymentResult = {
          paymentIntent,
          amount:   scenario.amountInPence,
          currency: scenario.currency || 'gbp',
          scenario,
        };
      } else {
        // Mock payment (development / Stripe not installed)
        await new Promise(r => setTimeout(r, 1800));
        paymentResult = {
          paymentIntent: { id: `pi_mock_${Date.now()}`, status: 'succeeded' },
          amount:        scenario.amountInPence,
          currency:      scenario.currency || 'gbp',
          scenario,
        };
      }

      setStep('success');
      onPaymentSuccess?.(paymentResult);
    } catch (e) {
      setStep('failed');
      onPaymentFailure?.(e);
      Alert.alert('Payment Failed', e.message || 'Your payment could not be processed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // ── RENDER ──────────────────────────────────────────────────────────────────

  if (step === 'processing') {
    return (
      <SafeAreaView style={styles.centeredScreen}>
        <ActivityIndicator size="large" color={C.primary}/>
        <Text style={styles.processingTitle}>Processing Payment...</Text>
        <Text style={styles.processingSubtitle}>Please do not close this screen</Text>
      </SafeAreaView>
    );
  }

  if (step === 'success') {
    return (
      <SafeAreaView style={styles.centeredScreen}>
        <View style={styles.successCircle}>
          <MaterialCommunityIcons name="check-circle" size={64} color={C.success}/>
        </View>
        <Text style={styles.successTitle}>Payment Successful!</Text>
        <Text style={styles.successAmount}>{fmtCurrency(scenario.amountInPence)}</Text>
        <Text style={styles.successDesc}>{scenario.name}</Text>
        <Text style={styles.successNote}>An invoice has been generated and sent to your account.</Text>
        <TouchableOpacity style={styles.doneBtn} onPress={onCancel}>
          <Text style={styles.doneBtnText}>Done</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex:1, backgroundColor: C.bg }}>
      {/* Header */}
      <LinearGradient colors={[C.primary, C.p2]} style={styles.header}>
        <TouchableOpacity style={styles.backBtn} onPress={onCancel}>
          <Ionicons name="close" size={22} color="#fff"/>
        </TouchableOpacity>
        <View style={{ flex:1 }}>
          <Text style={styles.headerTitle}>{C.label}</Text>
          <Text style={styles.headerSub}>Secure checkout · Protected by Stripe</Text>
        </View>
        <MaterialCommunityIcons name="lock" size={20} color="rgba(255,255,255,0.6)"/>
      </LinearGradient>

      <ScrollView contentContainerStyle={{ padding:20 }} keyboardShouldPersistTaps="handled">
        {/* Order summary */}
        <View style={styles.orderCard}>
          <View style={styles.orderHeader}>
            <MaterialCommunityIcons name="receipt" size={20} color={C.primary}/>
            <Text style={styles.orderTitle}>Order Summary</Text>
          </View>
          <View style={styles.orderRow}>
            <Text style={styles.orderDesc}>{scenario.name}</Text>
            <Text style={styles.orderAmount}>{fmtCurrency(scenario.amountInPence)}</Text>
          </View>
          {C.vat && (
            <View style={styles.orderRow}>
              <Text style={styles.orderKey}>VAT (20%)</Text>
              <Text style={styles.orderVal}>{fmtCurrency(scenario.amountInPence * 0.2)}</Text>
            </View>
          )}
          <View style={[styles.orderRow, styles.orderTotal]}>
            <Text style={styles.orderTotalKey}>Total</Text>
            <Text style={styles.orderTotalVal}>{fmtCurrency(scenario.amountInPence * (C.vat ? 1.2 : 1))}</Text>
          </View>
        </View>

        {/* Free Trial Offer */}
        {showTrial && trialEligible && (
          <View style={styles.trialCard}>
            <LinearGradient colors={[C.purple, '#9F67FF']} style={styles.trialGrad}>
              <MaterialCommunityIcons name="gift-outline" size={28} color="#fff"/>
              <View style={{ flex:1, marginLeft:12 }}>
                <Text style={styles.trialTitle}>Start Free for 14 Days</Text>
                <Text style={styles.trialSub}>Full access, no charge until trial ends</Text>
              </View>
            </LinearGradient>
            <View style={styles.trialBody}>
              {['All premium features included','No credit card charge now','Cancel anytime before trial ends','Invoice only after trial period'].map((f,i) => (
                <View key={i} style={styles.trialFeature}>
                  <MaterialCommunityIcons name="check-circle" size={14} color={C.success}/>
                  <Text style={styles.trialFeatureText}>{f}</Text>
                </View>
              ))}
              <View style={styles.trialActions}>
                <TouchableOpacity style={styles.trialBtn} onPress={handleStartFreeTrial} disabled={processing}>
                  {processing ? <ActivityIndicator size="small" color="#fff"/> : <Text style={styles.trialBtnText}>Start Free Trial</Text>}
                </TouchableOpacity>
                <TouchableOpacity style={styles.trialSkip} onPress={() => setShowTrial(false)}>
                  <Text style={styles.trialSkipText}>Pay Now Instead</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        )}

        {/* Card form */}
        {!showTrial && (
          <>
            <View style={styles.cardSection}>
              <View style={styles.cardSectionHeader}>
                <MaterialCommunityIcons name="credit-card-outline" size={18} color={C.primary}/>
                <Text style={styles.cardSectionTitle}>Card Details</Text>
                <View style={styles.securedBadge}>
                  <MaterialCommunityIcons name="shield-check" size={12} color={C.success}/>
                  <Text style={styles.securedText}>Secured</Text>
                </View>
              </View>

              {stripeAvailable && CardField ? (
                <CardField
                  postalCodeEnabled={false}
                  style={styles.stripeCardField}
                  cardStyle={stripeCardStyle}
                  onCardChange={setCardDetails}
                />
              ) : (
                <MockCardField onCardChange={setCardDetails} C={C} styles={styles}/>
              )}

              {initError && !clientSecret?.includes('mock') && (
                <View style={styles.warningBanner}>
                  <MaterialCommunityIcons name="information" size={14} color={C.warning}/>
                  <Text style={styles.warningText}>Using test mode — real payments disabled</Text>
                </View>
              )}
            </View>

            {/* Trust badges */}
            <View style={styles.trustRow}>
              {[
                { icon:'lock', label:'256-bit SSL' },
                { icon:'shield-check', label:'PCI DSS' },
                { icon:'credit-card-check', label:'3D Secure' },
              ].map((b,i) => (
                <View key={i} style={styles.trustBadge}>
                  <MaterialCommunityIcons name={b.icon} size={16} color={C.sub}/>
                  <Text style={styles.trustBadgeText}>{b.label}</Text>
                </View>
              ))}
            </View>

            {/* Pay button */}
            <TouchableOpacity style={styles.payBtn} onPress={handlePay} disabled={processing}>
              {processing ? (
                <ActivityIndicator size="small" color="#fff"/>
              ) : (
                <>
                  <MaterialCommunityIcons name="lock" size={18} color="#fff"/>
                  <Text style={styles.payBtnText}>Pay {fmtCurrency(scenario.amountInPence)}</Text>
                </>
              )}
            </TouchableOpacity>

            <Text style={styles.stripeNote}>Powered by Stripe · Your card is not stored on our servers</Text>

            {trialEligible && (
              <TouchableOpacity onPress={() => setShowTrial(true)} style={styles.showTrialLink}>
                <Text style={styles.showTrialLinkText}>🎁 Start free 14-day trial instead</Text>
              </TouchableOpacity>
            )}
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const stripeCardStyle = {
  backgroundColor: '#FFFFFF',
  textColor:       '#1E293B',
  placeholderColor:'#94A3B8',
  fontSize:        16,
  borderRadius:    12,
};

const makeStyles = (C) => StyleSheet.create({
  centeredScreen:      { flex:1, justifyContent:'center', alignItems:'center', backgroundColor:'#fff', gap:12, padding:32 },
  errorContainer:      { flex:1, justifyContent:'center', alignItems:'center', gap:12, backgroundColor:C.bg },
  errorText:           { fontSize:15, color:C.danger, fontWeight:'600' },
  processingTitle:     { fontSize:18, fontWeight:'700', color:C.text },
  processingSubtitle:  { fontSize:13, color:C.sub },
  successCircle:       { width:100, height:100, borderRadius:50, backgroundColor:'#D1FAE5', justifyContent:'center', alignItems:'center', marginBottom:8 },
  successTitle:        { fontSize:24, fontWeight:'800', color:C.text },
  successAmount:       { fontSize:32, fontWeight:'900', color:C.primary },
  successDesc:         { fontSize:14, color:C.sub },
  successNote:         { fontSize:12, color:C.light, textAlign:'center', marginTop:8 },
  doneBtn:             { marginTop:20, backgroundColor:C.primary, paddingHorizontal:40, paddingVertical:14, borderRadius:16 },
  doneBtnText:         { color:'#fff', fontWeight:'700', fontSize:16 },
  header:              { flexDirection:'row', alignItems:'center', paddingHorizontal:16, paddingVertical:14, paddingTop:20, gap:12 },
  backBtn:             { width:36, height:36, borderRadius:18, backgroundColor:'rgba(255,255,255,0.15)', justifyContent:'center', alignItems:'center' },
  headerTitle:         { color:'#fff', fontSize:17, fontWeight:'700' },
  headerSub:           { color:'rgba(255,255,255,0.7)', fontSize:11 },
  orderCard:           { backgroundColor:'#fff', borderRadius:16, padding:16, marginBottom:16, shadowColor:'#000', shadowOpacity:0.06, shadowRadius:8, elevation:3 },
  orderHeader:         { flexDirection:'row', alignItems:'center', gap:8, marginBottom:14, paddingBottom:12, borderBottomWidth:1, borderBottomColor:C.border },
  orderTitle:          { fontSize:15, fontWeight:'700', color:C.text },
  orderRow:            { flexDirection:'row', justifyContent:'space-between', alignItems:'center', paddingVertical:6 },
  orderDesc:           { fontSize:13, color:C.text, fontWeight:'600', flex:1 },
  orderAmount:         { fontSize:15, fontWeight:'700', color:C.text },
  orderKey:            { fontSize:13, color:C.sub },
  orderVal:            { fontSize:13, color:C.sub },
  orderTotal:          { borderTopWidth:1.5, borderTopColor:C.primary, marginTop:6, paddingTop:10 },
  orderTotalKey:       { fontSize:15, fontWeight:'700', color:C.text },
  orderTotalVal:       { fontSize:18, fontWeight:'900', color:C.primary },
  trialCard:           { backgroundColor:'#fff', borderRadius:16, marginBottom:16, overflow:'hidden', shadowColor:'#000', shadowOpacity:0.08, shadowRadius:10, elevation:4 },
  trialGrad:           { flexDirection:'row', alignItems:'center', padding:18 },
  trialTitle:          { color:'#fff', fontSize:16, fontWeight:'800' },
  trialSub:            { color:'rgba(255,255,255,0.8)', fontSize:12, marginTop:2 },
  trialBody:           { padding:16, gap:10 },
  trialFeature:        { flexDirection:'row', alignItems:'center', gap:8 },
  trialFeatureText:    { fontSize:13, color:C.text },
  trialActions:        { marginTop:12, gap:10 },
  trialBtn:            { backgroundColor:C.purple, paddingVertical:14, borderRadius:14, alignItems:'center' },
  trialBtnText:        { color:'#fff', fontWeight:'700', fontSize:15 },
  trialSkip:           { alignItems:'center', paddingVertical:8 },
  trialSkipText:       { color:C.sub, fontSize:13 },
  cardSection:         { backgroundColor:'#fff', borderRadius:16, padding:18, marginBottom:16, shadowColor:'#000', shadowOpacity:0.06, shadowRadius:8, elevation:3 },
  cardSectionHeader:   { flexDirection:'row', alignItems:'center', gap:8, marginBottom:14 },
  cardSectionTitle:    { flex:1, fontSize:15, fontWeight:'700', color:C.text },
  securedBadge:        { flexDirection:'row', alignItems:'center', gap:4, backgroundColor:'#D1FAE5', paddingHorizontal:8, paddingVertical:3, borderRadius:10 },
  securedText:         { fontSize:10, color:C.success, fontWeight:'700' },
  stripeCardField:     { height:54, marginVertical:4 },
  mockCard:            { backgroundColor:C.bg, borderRadius:12, padding:16, borderWidth:1.5, borderColor:C.border, gap:6 },
  mockCardText:        { fontSize:14, color:C.text, fontFamily:'monospace', letterSpacing:1 },
  mockCardNote:        { fontSize:11, color:C.light, fontStyle:'italic' },
  warningBanner:       { flexDirection:'row', alignItems:'center', gap:6, backgroundColor:'#FEF3C7', padding:10, borderRadius:10, marginTop:10 },
  warningText:         { fontSize:12, color:'#92400E', flex:1 },
  trustRow:            { flexDirection:'row', justifyContent:'center', gap:20, marginBottom:20 },
  trustBadge:          { alignItems:'center', gap:4 },
  trustBadgeText:      { fontSize:10, color:C.light, fontWeight:'600' },
  payBtn:              { backgroundColor:C.primary, flexDirection:'row', alignItems:'center', justifyContent:'center', gap:10, paddingVertical:16, borderRadius:16, shadowColor:C.primary, shadowOpacity:0.4, shadowRadius:12, elevation:6 },
  payBtnText:          { color:'#fff', fontWeight:'800', fontSize:17 },
  stripeNote:          { textAlign:'center', fontSize:11, color:C.light, marginTop:12 },
  showTrialLink:       { alignItems:'center', marginTop:14 },
  showTrialLinkText:   { color:C.purple, fontSize:13, fontWeight:'700' },
});